import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth';
import { api } from '../../services/api';
import LanguageSelector from '../Common/LanguageSelector';
import DataImport from './DataImport';
import ECIScraper from './ECIScraper';
import AddConstituency from './AddConstituency';
import AddCandidate from './AddCandidate';
import ViewData from './ViewData';
import SliderManagement from './SliderManagement'; 
import PartyList from './PartyList';
import AdminVoterCertificates from './AdminVoterCertificates';
import AdminVoterSurveys from './AdminVoterSurveys';
import AdminPollData from './AdminPollData';
import AdminVoteCounting from './AdminVoteCounting'; // ✅ NEW IMPORT
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalConstituencies: 0,
    totalCandidates: 0,
    totalParties: 0,
    totalResults: 0,
    totalCertificates: 0,
    totalSurveys: 0
  });
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = authService.getUser();

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getStatistics();
      
      setStats({
        totalConstituencies: data.constituencies?.total || 0,
        totalCandidates: data.candidates || 0,
        totalParties: data.parties || 0,
        totalResults: data.totalResults || 0,
        totalCertificates: data.totalCertificates || 0,
        totalSurveys: data.totalSurveys || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm(t('auth.logout') + '?')) {
      authService.logout();
      navigate('/login');
    }
  };

  const menuItems = [
    { 
      id: 'dashboard', 
      icon: 'home', 
      label: t('admin.dashboard') || 'Dashboard' 
    },
    { 
      id: 'slider', 
      icon: 'images', 
      label: t('admin.manageSliders') || 'Manage Sliders' 
    },
    { 
      id: 'party', 
      icon: 'flag', 
      label: t('admin.manageParties') || 'Manage Parties' 
    },
    { 
      id: 'add-constituency', 
      icon: 'map-marked-alt', 
      label: t('admin.addConstituency') || 'Add Constituency' 
    },
    { 
      id: 'add-candidate', 
      icon: 'user-plus', 
      label: t('admin.addCandidate') || 'Add Candidate' 
    },
    { 
      id: 'poll-data',
      icon: 'chart-pie',
      label: t('admin.pollData') || 'Poll Data'
    },
    { 
      id: 'vote-counting', // ✅ NEW MENU ITEM
      icon: 'vote-yea',
      label: t('admin.voteCounting') || 'Vote Counting'
    },
    { 
      id: 'voter-certificates',
      icon: 'id-card', 
      label: t('admin.voterCertificates') || 'Voter Certificates' 
    },
    { 
      id: 'voter-surveys',
      icon: 'poll', 
      label: t('admin.voterSurveys') || 'Voter Surveys' 
    },
    { 
      id: 'import', 
      icon: 'file-upload', 
      label: t('admin.importData') || 'Import Data' 
    },
    { 
      id: 'scraper', 
      icon: 'globe', 
      label: t('admin.eciScraper') || 'ECI Scraper' 
    },
    { 
      id: 'view', 
      icon: 'table', 
      label: t('admin.viewData') || 'View Data' 
    }
  ];

  const statCards = [
    { 
      label: t('dashboard.totalConstituencies') || 'Constituencies', 
      value: stats.totalConstituencies, 
      icon: 'map-marked-alt', 
      color: '#667eea' 
    },
    { 
      label: t('dashboard.totalCandidates') || 'Candidates', 
      value: stats.totalCandidates, 
      icon: 'users', 
      color: '#764ba2' 
    },
    { 
      label: t('dashboard.politicalParties') || 'Political Parties', 
      value: stats.totalParties, 
      icon: 'flag', 
      color: '#f093fb' 
    },
    { 
      label: t('dashboard.resultsRecorded') || 'Results Recorded', 
      value: stats.totalResults, 
      icon: 'chart-line', 
      color: '#4facfe' 
    },
    { 
      label: t('dashboard.voterCertificates') || 'Voter Certificates', 
      value: stats.totalCertificates, 
      icon: 'id-card', 
      color: '#11998e' 
    },
    { 
      label: t('dashboard.voterSurveys') || 'Voter Surveys', 
      value: stats.totalSurveys, 
      icon: 'poll-h', 
      color: '#fa709a' 
    }
  ];

  return (
    <div className="admin-dashboard">
      <button 
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle sidebar"
      >
        <i className={`fas fa-${sidebarOpen ? 'times' : 'bars'}`}></i>
      </button>

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-icon-admin">
            <img 
              src="/logo192.png" 
              alt="Bihar Election Logo" 
              className="admin-logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
          </div>
          <h2>{t('admin.title') || 'Admin Dashboard'}</h2>
        </div>

        <nav className="sidebar-menu">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id);
                if (window.innerWidth <= 768) setSidebarOpen(false);
              }}
              className={`menu-item ${activeSection === item.id ? 'active' : ''}`}
            >
              <i className={`fas fa-${item.icon}`}></i>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-button">
            <i className="fas fa-sign-out-alt"></i>
            <span>{t('auth.logout') || 'Logout'}</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="content-header">
          <div className="header-text">
            <h1>{t('admin.subtitle') || 'Admin Panel'}</h1>
            <p>{t('dashboard.secureAdmin') || 'Secure Administration'}</p>
          </div>
          <div className="header-actions">
            <LanguageSelector />
            <button
              onClick={() => navigate('/')}
              className="view-public-btn"
            >
              <i className="fas fa-eye"></i> 
              <span>{t('dashboard.viewPublicSite') || 'View Public Site'}</span>
            </button>
          </div>
        </header>

        {error && (
          <div className="error-alert">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
            <button onClick={() => setError(null)} className="close-btn">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {activeSection === 'dashboard' && (
          <div className="dashboard-content">
            <h2 className="section-title">{t('dashboard.overview') || 'Dashboard Overview'}</h2>
            
            <div className="stats-grid">
              {loading ? (
                <div className="loading-stats">
                  <div className="spinner-large"></div>
                  <p>{t('dashboard.loadingStats') || 'Loading Statistics...'}</p>
                </div>
              ) : (
                statCards.map((stat, idx) => (
                  <div 
                    key={idx} 
                    className="stat-card"
                    style={{ '--card-color': stat.color }}
                  >
                    <div className="stat-icon">
                      <i className={`fas fa-${stat.icon}`}></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{stat.value.toLocaleString()}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="action-buttons">
              <button onClick={loadStats} className="action-btn refresh">
                <i className="fas fa-sync-alt"></i> 
                {t('dashboard.refreshData') || 'Refresh Data'}
              </button>
              <button onClick={() => setActiveSection('slider')} className="action-btn slider">
                <i className="fas fa-images"></i> 
                {t('admin.manageSliders') || 'Manage Sliders'}
              </button>
              <button onClick={() => setActiveSection('party')} className="action-btn party">
                <i className="fas fa-flag"></i> 
                {t('admin.manageParties') || 'Manage Parties'}
              </button>
              <button onClick={() => setActiveSection('add-constituency')} className="action-btn constituency">
                <i className="fas fa-map-marked-alt"></i> 
                {t('admin.addConstituency') || 'Add Constituency'}
              </button>
              <button onClick={() => setActiveSection('add-candidate')} className="action-btn candidate">
                <i className="fas fa-user-plus"></i> 
                {t('admin.addCandidate') || 'Add Candidate'}
              </button>
              <button onClick={() => setActiveSection('poll-data')} className="action-btn poll">
                <i className="fas fa-chart-pie"></i> 
                {t('admin.pollData') || 'Poll Data'}
              </button>
              {/* ✅ NEW BUTTON */}
              <button onClick={() => setActiveSection('vote-counting')} className="action-btn vote-counting">
                <i className="fas fa-vote-yea"></i> 
                {t('admin.voteCounting') || 'Vote Counting'}
              </button>
              <button onClick={() => setActiveSection('voter-certificates')} className="action-btn certificates">
                <i className="fas fa-id-card"></i> 
                {t('admin.voterCertificates') || 'Voter Certificates'}
              </button>
              <button onClick={() => setActiveSection('voter-surveys')} className="action-btn surveys">
                <i className="fas fa-poll"></i> 
                {t('admin.voterSurveys') || 'Voter Surveys'}
              </button>
              <button onClick={() => setActiveSection('import')} className="action-btn import">
                <i className="fas fa-file-upload"></i> 
                {t('admin.importData') || 'Import Data'}
              </button>
              <button onClick={() => setActiveSection('scraper')} className="action-btn scraper">
                <i className="fas fa-globe"></i> 
                {t('admin.eciScraper') || 'ECI Scraper'}
              </button>
            </div>

            <div className="quick-info">
              <div className="info-card">
                <i className="fas fa-info-circle"></i>
                <div>
                  <h3>{t('dashboard.systemStatus') || 'System Status'}</h3>
                  <p>{t('dashboard.allOperational') || 'All Systems Operational'}</p>
                </div>
              </div>
              <div className="info-card">
                <i className="fas fa-clock"></i>
                <div>
                  <h3>{t('dashboard.lastUpdated') || 'Last Updated'}</h3>
                  <p>{new Date().toLocaleString()}</p>
                </div>
              </div>
              <div className="info-card">
                <i className="fas fa-database"></i>
                <div>
                  <h3>{t('dashboard.database') || 'Database'}</h3>
                  <p>{loading ? 'Checking...' : 'Connected & Healthy'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'slider' && (
          <div className="section-content">
            <SliderManagement />
          </div>
        )}

        {activeSection === 'party' && (
          <div className="section-content">
            <PartyList />
          </div>
        )}

        {activeSection === 'add-constituency' && (
          <div className="section-content">
            <AddConstituency onSuccess={loadStats} />
          </div>
        )}

        {activeSection === 'add-candidate' && (
          <div className="section-content">
            <AddCandidate onSuccess={loadStats} />
          </div>
        )}

        {activeSection === 'poll-data' && (
          <div className="section-content">
            <AdminPollData onSuccess={loadStats} />
          </div>
        )}

        {/* ✅ NEW SECTION */}
        {activeSection === 'vote-counting' && (
          <div className="section-content">
            <AdminVoteCounting onSuccess={loadStats} />
          </div>
        )}

        {activeSection === 'voter-certificates' && (
          <div className="section-content">
            <AdminVoterCertificates />
          </div>
        )}

        {activeSection === 'voter-surveys' && (
          <div className="section-content">
            <AdminVoterSurveys />
          </div>
        )}

        {activeSection === 'import' && (
          <div className="section-content">
            <DataImport onSuccess={loadStats} />
          </div>
        )}

        {activeSection === 'scraper' && (
          <div className="section-content">
            <ECIScraper onSuccess={loadStats} />
          </div>
        )}
        
        {activeSection === 'view' && (
          <div className="section-content">
            <ViewData />
          </div>
        )}
      </main>

      {sidebarOpen && window.innerWidth <= 768 && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Dashboard;
